import { Component, Inject, Input, OnInit, ViewChild, SimpleChanges } from '@angular/core';
import { Tooltip, ChartConfiguration, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

import { ThemeService } from 'src/app/services/ThemeService';
import { UsefulService } from 'src/app/services/UsefulService';

declare module 'chart.js' {
    interface TooltipPositionerMap {
        cursor: any;
    }
}

@Component({
    selector: 'stLineChart',
    templateUrl: './st-line-chart.template.html',
    styleUrls: ['./st-line-chart.component.scss'],
    standalone: false
})
export class StLineChartComponent implements OnInit {
    @Input() datasets: ChartConfiguration<'line'>['data']['datasets'] = [];
    @Input() labels: string[] = [];
    @Input() displayLegend: boolean = true;
    @Input() yAxisLabel: string = '';
    @Input() xAxisLabel: string = '';
    @Input() externalTooltipHandler: (context: any) => void;

    @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

    public showMobileUi = false;

    public lineChartOptions: ChartOptions<'line'> = {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                display: this.displayLegend,
            },
            tooltip: {
                enabled: true,
                position: 'cursor',
            },
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            x: {
                title: {
                    display: false,
                    text: this.xAxisLabel,
                },
                grid: {
                    display: false,
                },
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: this.yAxisLabel,
                },
            },
        },
    };

    public lineChartData: ChartConfiguration<'line'>['data'] = {
        datasets: [],
        labels: [],
    };

    constructor(
        // even though the services are not explicitly used here, they might be used by the externalTooltipHandler input
        @Inject(UsefulService) private usefulService: UsefulService,
        @Inject(ThemeService) private themeService: ThemeService,
    ) {
        Tooltip.positioners.cursor = (chartElements: any, coordinates: any) => {
            return coordinates;
        };
    }

    ngOnInit(): void {
        this.showMobileUi = this.usefulService.showMobileUi;

        this.lineChartOptions.plugins.legend.display = this.displayLegend;
        if (this.xAxisLabel) {
            this.lineChartOptions.scales.x.title.display = true;
            this.lineChartOptions.scales.x.title.text = this.xAxisLabel;
        }
        if (this.yAxisLabel) {
            this.lineChartOptions.scales.y.title.display = true;
            this.lineChartOptions.scales.y.title.text = this.yAxisLabel;
        }

        if (this.externalTooltipHandler) {
            this.lineChartOptions.plugins.tooltip.enabled = false;
            this.lineChartOptions.plugins.tooltip.external = this.externalTooltipHandler.bind(this);
        }

        this.lineChartData.datasets = this.datasets.map((dataset) => ({
            ...dataset,
            backgroundColor: dataset.backgroundColor || 'rgba(0, 123, 255, 0.2)',
            borderColor: dataset.borderColor || 'rgba(0, 123, 255, 1)',
            borderWidth: dataset.borderWidth !== undefined ? dataset.borderWidth : 2,
            pointRadius: dataset.pointRadius !== undefined ? dataset.pointRadius : 0,
            pointBackgroundColor: dataset.borderColor || 'rgba(0, 123, 255, 1)',
        }));
        this.lineChartData.labels = this.labels;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.externalTooltipHandler && !changes.externalTooltipHandler.isFirstChange()) {
            this.lineChartOptions.plugins.tooltip.enabled = false;
            this.lineChartOptions.plugins.tooltip.external = this.externalTooltipHandler.bind(this);
        }
        // this._setColors();
        this._updateChart();
    }

    private _updateChart() {
        this.lineChartData.datasets = this.datasets.map((dataset) => ({
            ...dataset,
            backgroundColor: dataset.backgroundColor || 'rgba(0, 123, 255, 0.2)',
            borderColor: dataset.borderColor || 'rgba(0, 123, 255, 1)',
            borderWidth: dataset.borderWidth !== undefined ? dataset.borderWidth : 2,
            pointRadius: dataset.pointRadius !== undefined ? dataset.pointRadius : 0,
            pointBackgroundColor: dataset.borderColor || 'rgba(0, 123, 255, 1)',
        }));

        this.chart?.update();
    }
}
